import { useEffect, useRef, useState } from 'react';
import Panzoom, { type PanzoomObject } from '@panzoom/panzoom';
import { Flex, Icon, IconButton, Text, Tooltip, icon as iconSize } from '@nula/core-ui';
import { renderMermaid, type MermaidResult } from '../utils/render-mermaid';
import { shouldZoomOnWheel, normalizeMermaidSvg } from '../utils/mermaid-pan-zoom';
import styles from './mermaid-diagram.module.css';

export interface MermaidDiagramProps {
  /** The mermaid source for this code block. */
  code: string;
}

/** Debounce window before (re-)rendering the diagram as the source changes. */
const RENDER_DEBOUNCE_MS = 300;

let diagramSeq = 0;

/**
 * Renders mermaid `code` as a diagram. Mounted only when the block's toolbar
 * toggle is ON (see mermaid-extension + mermaid-view-store) — when off, the
 * normal editable code block shows instead and this never mounts. Rendering is
 * debounced and isolated to this component; it never touches the editor's
 * content DOM. Invalid diagrams show the error + source, never a blank.
 *
 * The rendered SVG is wrapped with pan/zoom: drag to pan, ⌘/Ctrl+wheel to zoom
 * (a plain wheel still scrolls the page), and a reset control restores the fit.
 */
export function MermaidDiagram({ code }: MermaidDiagramProps) {
  const [result, setResult] = useState<MermaidResult | null>(null);
  const [loading, setLoading] = useState(true);
  // Stable, unique id base for mermaid's SVG scoping (must be DOM-id safe).
  const idRef = useRef(`mermaid-${(diagramSeq += 1)}`);
  const diagramRef = useRef<HTMLDivElement>(null);
  const panzoomRef = useRef<PanzoomObject | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(() => {
      void renderMermaid(idRef.current, code).then((r) => {
        if (cancelled) return;
        setResult(r);
        setLoading(false);
      });
    }, RENDER_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [code]);

  // Attach pan/zoom to the SVG wrapper (not the SVG itself) so the SVG is free
  // to scale to 100% width via CSS while panzoom drives the wrapper's transform.
  // Re-runs whenever the rendered result changes, since each render replaces it.
  useEffect(() => {
    const wrapper = diagramRef.current;
    if (!wrapper || !result?.ok) return;
    if (!wrapper.querySelector('svg')) return;

    const panzoom = Panzoom(wrapper, { maxScale: 8, minScale: 0.5, cursor: 'grab' });
    panzoomRef.current = panzoom;

    const onWheel = (event: WheelEvent) => {
      if (!shouldZoomOnWheel(event)) return;
      panzoom.zoomWithWheel(event);
    };
    wrapper.addEventListener('wheel', onWheel);

    return () => {
      wrapper.removeEventListener('wheel', onWheel);
      panzoom.destroy();
      panzoomRef.current = null;
    };
  }, [result]);

  return (
    <div className={styles.root} contentEditable={false}>
      {loading ? (
        <Flex align="center" justify="center" className={styles.state}>
          <Text size="sm" className={styles.muted}>Rendering diagram…</Text>
        </Flex>
      ) : result && !result.ok ? (
        <Flex vertical gap="xs" className={styles.error}>
          <Text size="xs" className={styles.errorMsg}>{result.error}</Text>
          <pre className={styles.code}>{code}</pre>
        </Flex>
      ) : result && result.ok ? (
        <div className={styles.viewport}>
          {/* mermaid renders with securityLevel 'strict' → SVG is sanitized. */}
          <div className={styles.diagram}>
            <div
              ref={diagramRef}
              className={styles.svgWrap}
              dangerouslySetInnerHTML={{ __html: normalizeMermaidSvg(result.svg) }}
            />
            <Flex className={styles.reset}>
              <Tooltip title="Reset view" placement="top" mouseEnterDelay={600}>
                <IconButton
                  size="sm"
                  transparent
                  pill
                  aria-label="Reset diagram view"
                  onClick={() => panzoomRef.current?.reset()}
                >
                  <Icon name="zoom_out_map" size={iconSize.sm} weight={300} />
                </IconButton>
              </Tooltip>
            </Flex>
          </div>
        </div>
      ) : null}
    </div>
  );
}
